import { onlineManager, type QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { Persister } from "@tanstack/query-persist-client-core";
import { toast } from "@/hooks/use-toast";
import { readRollupWarning } from "@/lib/rollup-warning";
import { authUserSchema } from "./auth-user";
import { DB_RETRYABLE_CONFLICT_CODE } from "@shared/db-retryable-conflict";

export const SHOP_FLOOR_PERSIST_KEY = "production-hub-shop-floor-offline-v1";
export const SHOP_FLOOR_PERSIST_BUSTER = "shop-floor-v1";

export const SHOP_FLOOR_MUTATION_KEYS = {
  markScreenBurned: ["shop-floor", "screenmaster", "mark-burned"] as const,
  markScreenShared: ["shop-floor", "screenmaster", "mark-shared"] as const,
  undoSharedScreen: ["shop-floor", "screenmaster", "undo-shared"] as const,
};

export interface MarkScreenBurnedVariables {
  submittingUserId?: string;
  specId: string;
  burnedBy?: string;
  version: number;
  meta?: {
    orderNumber: string;
    colorName: string;
    meshCount: string;
    location: string;
    burnMode: string;
  };
}

export interface MarkScreenSharedVariables {
  submittingUserId?: string;
  specId: string;
  sharedWithRunId: string;
  screenNotes: string;
  version: number;
}

export interface UndoSharedScreenVariables {
  submittingUserId?: string;
  specId: string;
  version: number;
}

export function createShopFloorPersister(storage: Storage | undefined = typeof window !== "undefined" ? window.localStorage : undefined): Persister {
  return createSyncStoragePersister({
    storage,
    key: SHOP_FLOOR_PERSIST_KEY,
  });
}

type ShopFloorMutation = ReturnType<QueryClient["getMutationCache"]>["getAll"] extends () => (infer M)[] ? M : never;
const replayPermits = new WeakMap<QueryClient, WeakSet<ShopFloorMutation>>();
const lastRecoveryNotice = new WeakMap<QueryClient, string>();
// HUB-572: while an account switch is unconfirmed the screen shows a held identity, so no
// shop-floor action may start or continue for that client. Set by the user context.
const identityHeldClients = new WeakSet<QueryClient>();

export function setShopFloorReplayHold(client: QueryClient, held: boolean) {
  if (held) identityHeldClients.add(client);
  else identityHeldClients.delete(client);
}

function isShopFloorMutation(mutation: ShopFloorMutation): boolean {
  return Object.values(SHOP_FLOOR_MUTATION_KEYS).some(key =>
    JSON.stringify(key) === JSON.stringify(mutation.options.mutationKey));
}

function actionOwner(variables: unknown): string | undefined {
  if (!variables || typeof variables !== "object" || !("submittingUserId" in variables)) return undefined;
  return typeof variables.submittingUserId === "string" && variables.submittingUserId ? variables.submittingUserId : undefined;
}

class OfflineIdentityPause extends Error {}

interface ConfirmedIdentity { id: string; impersonating: boolean }

const VIEW_ONLY_REFUSAL = JSON.stringify({
  code: "IMPERSONATION_VIEW_ONLY",
  message: "View-only while impersonating — switch back to your account to make changes",
});
const ACCOUNT_CHANGED_REFUSAL = JSON.stringify({
  code: "SESSION_IDENTITY_CHANGED",
  message: "Your account changed in another tab. The page has been updated — please try again.",
});

// HUB-583: viewing as an employee is view-only. An action created now would be saved
// under that employee's name and could later replay as if they had done it, so it is
// refused before it is persisted or sent. Restored work never runs onMutate.
async function refuseWhileImpersonating(client: QueryClient) {
  if (!client.getQueryData<{ impersonating?: boolean }>(["/api/auth/me"])?.impersonating) return;
  // The cached identity can stay stale for minutes after another tab switches back, so
  // ask the server before refusing. An unconfirmed read stays refused (fail-safe).
  const confirmed = await confirmedReplayIdentity().catch(() => undefined);
  if (confirmed && !confirmed.impersonating) {
    // This click still carries the stale author, so it cannot proceed; refresh the app's
    // identity so the screen shows the real account and the next click is attributed to it.
    void client.invalidateQueries({ queryKey: ["/api/auth/me"], exact: true });
    throw new Error(`409: ${ACCOUNT_CHANGED_REFUSAL}`);
  }
  throw new Error(`403: ${VIEW_ONLY_REFUSAL}`);
}

function installReplayGuard(client: QueryClient) {
  if (replayPermits.has(client)) return;
  const permits = new WeakSet<ShopFloorMutation>();
  replayPermits.set(client, permits);
  const cache = client.getMutationCache();
  const canRun = cache.canRun.bind(cache);
  // The hold is checked here, not only in resumePausedShopFloorMutations: core focus/online resume
  // and retry timers continue already-permitted actions without passing through that wrapper.
  // A blocked mutation pauses with its variables intact and continues once the hold is released.
  cache.canRun = mutation => canRun(mutation) && (!isShopFloorMutation(mutation)
    || (permits.has(mutation) && !identityHeldClients.has(client)));
  // Core's automatic focus/online resume also passes through this guard. An
  // unowned restored action never reaches its mutation function or loses pause.
  cache.subscribe(event => {
    if (event.type === "updated" && event.action.type === "pending" && isShopFloorMutation(event.mutation)) {
      const owner = actionOwner(event.mutation.state.variables);
      const current = client.getQueryData<{ id: string; impersonating?: boolean }>(["/api/auth/me"]);
      if (owner && owner === current?.id && !current.impersonating && onlineManager.isOnline()) permits.add(event.mutation);
    }
  });
}

async function confirmedReplayIdentity(): Promise<ConfirmedIdentity | undefined> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch("/api/auth/me", { credentials: "include", cache: "no-store", signal: controller.signal });
    if (response.status === 401) return undefined;
    if (response.status !== 200) throw new Error("Identity service unavailable");
    const user = authUserSchema.safeParse(await response.json());
    if (!user.success) throw new Error("Identity response incomplete");
    return { id: user.data.id, impersonating: user.data.impersonating === true };
  } catch {
    // A temporary read failure is not proof of a different employee. Live
    // actions use the normal bounded backoff and surface an error if it persists.
    throw new Error("Could not verify who is signed in. Please try again.");
  } finally { clearTimeout(timeout); }
}

function holdAction(client: QueryClient, variables: unknown): never {
  for (const mutation of client.getMutationCache().getAll()) {
    if (mutation.state.variables === variables) replayPermits.get(client)?.delete(mutation);
  }
  throw new OfflineIdentityPause("Offline action is waiting for its author");
}

async function sendOwnedAction(client: QueryClient, variables: unknown, url: string, data: unknown): Promise<Response> {
  const owner = actionOwner(variables);
  const confirmed = owner ? await confirmedReplayIdentity() : undefined;
  if (!owner || confirmed?.id !== owner) return holdAction(client, variables);
  // HUB-583: viewing as the author is view-only; their work waits for them to sign in.
  if (confirmed.impersonating) return holdAction(client, variables);
  // A switch may have started while the identity read was in flight; never send during the hold.
  if (identityHeldClients.has(client)) return holdAction(client, variables);
  const response = await fetch(url, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", "X-Submitting-User-Id": owner },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    // Global auth runs before the route's author check. A session expiring in
    // this gap must leave the action recoverable, just like a changed employee.
    if (response.status === 401) return holdAction(client, variables);
    const text = (await response.text()) || response.statusText;
    // The cookie can change after the GET. Server rejects before any write;
    // put this action back on hold instead of consuming someone else's work.
    if (response.status === 409 && text.includes('"code":"OFFLINE_ACTION_IDENTITY_MISMATCH"')) return holdAction(client, variables);
    // HUB-583: impersonation can start in another tab after the identity read. The
    // view-only block answers before the route's author check; keep the author's work.
    if (response.status === 403 && text.includes('"code":"IMPERSONATION_VIEW_ONLY"')) return holdAction(client, variables);
    throw new Error(`${response.status}: ${text}`);
  }
  return response;
}

function isVersionConflictForReplay(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  if (!msg.startsWith("409:")) return false;
  try {
    const jsonPart = msg.replace(/^\d+:\s*/, "");
    const parsed = JSON.parse(jsonPart) as { conflict?: unknown };
    return Boolean(parsed.conflict);
  } catch {
    return false;
  }
}

// 4xx responses are deterministic rejections (e.g. 400 "Not enough coated screens",
// 409 conflicts): replaying them can never succeed, so retrying only delays the
// component's onError toast by ~7s of backoff while the operator sees nothing (PRO-372).
// Network failures and 5xx remain retryable — that is the offline-replay feature's job.
function isDeterministicClientErrorForReplay(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /^4\d\d:/.test(msg) && !isRetryableDbConflictForReplay(msg);
}

// HUB-609: a deadlock/serialization 409 means Postgres rolled the whole save back, so replaying it
// is safe and usually succeeds — keep the retry it got as a 500 before. Only the explicit server
// marker opts in; every other 409 stays a deterministic rejection.
function isRetryableDbConflictForReplay(msg: string): boolean {
  if (!msg.startsWith("409:")) return false;
  try {
    const parsed = JSON.parse(msg.replace(/^\d+:\s*/, "")) as { code?: unknown };
    return parsed.code === DB_RETRYABLE_CONFLICT_CODE;
  } catch {
    return false;
  }
}

function shouldRetryReplay(failureCount: number, error: unknown): boolean {
  // holdAction revoked canRun before throwing: this retry enters a paused
  // retryer, preserving variables. It cannot issue another GET without a permit.
  return error instanceof OfflineIdentityPause || (!isDeterministicClientErrorForReplay(error) && failureCount < 3);
}

function invalidateScreenmasterState(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["/api/screenmaster-queue"] });
  queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
  queryClient.invalidateQueries({ queryKey: ["/api/screen-inventory"] });
}

function handleReplayError(error: unknown, queryClient: QueryClient) {
  if (isVersionConflictForReplay(error)) {
    invalidateScreenmasterState(queryClient);
  }
}

async function handleReplaySuccess(
  response: Response,
  queryClient: QueryClient,
  warningTitle: string,
) {
  invalidateScreenmasterState(queryClient);
  const rollupWarning = await readRollupWarning(response);
  if (rollupWarning) {
    toast({
      title: warningTitle,
      description: rollupWarning,
      variant: "destructive",
      severity: "warning",
    });
  }
}

export function registerShopFloorMutationDefaults(queryClient: QueryClient) {
  installReplayGuard(queryClient);
  queryClient.setMutationDefaults(SHOP_FLOOR_MUTATION_KEYS.markScreenBurned, {
    mutationFn: (variables: MarkScreenBurnedVariables) =>
      sendOwnedAction(queryClient, variables, `/api/screens/${variables.specId}/mark-burned`, { burnedBy: variables.burnedBy, version: variables.version }),
    onMutate: () => refuseWhileImpersonating(queryClient),
    networkMode: "online",
    retry: shouldRetryReplay,
    retryDelay: (count, error) => error instanceof OfflineIdentityPause ? 0 : Math.min(1000 * 2 ** count, 30_000),
    onSuccess: (response) => handleReplaySuccess(
      response,
      queryClient,
      "Screen marked as burned — but the milestone may be out of date",
    ),
    onError: (error) => handleReplayError(error, queryClient),
  });

  queryClient.setMutationDefaults(SHOP_FLOOR_MUTATION_KEYS.markScreenShared, {
    mutationFn: (variables: MarkScreenSharedVariables) =>
      sendOwnedAction(queryClient, variables, `/api/screens/${variables.specId}/mark-shared`, { sharedWithRunId: variables.sharedWithRunId, screenNotes: variables.screenNotes, version: variables.version }),
    onMutate: () => refuseWhileImpersonating(queryClient),
    networkMode: "online",
    retry: shouldRetryReplay,
    retryDelay: (count, error) => error instanceof OfflineIdentityPause ? 0 : Math.min(1000 * 2 ** count, 30_000),
    onSuccess: (response) => handleReplaySuccess(
      response,
      queryClient,
      "Screen marked as shared — but the milestone may be out of date",
    ),
    onError: (error) => handleReplayError(error, queryClient),
  });

  queryClient.setMutationDefaults(SHOP_FLOOR_MUTATION_KEYS.undoSharedScreen, {
    mutationFn: (variables: UndoSharedScreenVariables) =>
      sendOwnedAction(queryClient, variables, `/api/screens/${variables.specId}/undo-shared`, { version: variables.version }),
    onMutate: () => refuseWhileImpersonating(queryClient),
    networkMode: "online",
    retry: shouldRetryReplay,
    retryDelay: (count, error) => error instanceof OfflineIdentityPause ? 0 : Math.min(1000 * 2 ** count, 30_000),
    onSuccess: (response) => handleReplaySuccess(
      response,
      queryClient,
      "Share undone — but the milestone may be out of date",
    ),
    onError: (error) => handleReplayError(error, queryClient),
  });
}

export async function resumePausedShopFloorMutations(queryClient: QueryClient) {
  installReplayGuard(queryClient);
  if (identityHeldClients.has(queryClient)) return; // Work stays paused; releasing the hold resumes it.
  const paused = queryClient.getMutationCache().getAll().filter(m => isShopFloorMutation(m) && m.state.isPaused);
  if (!paused.length || !onlineManager.isOnline()) return;
  let current: ConfirmedIdentity | undefined;
  try { current = await confirmedReplayIdentity(); }
  catch { return; } // Restored work remains paused until identity can be confirmed.
  const currentId = current?.id;
  // HUB-583: viewing as an employee is view-only, so even their own saved work stays paused.
  const viewingAs = current?.impersonating === true;
  const held: ShopFloorMutation[] = [];
  for (const mutation of paused) {
    const owner = actionOwner(mutation.state.variables);
    if (owner && owner === currentId && !viewingAs) {
      replayPermits.get(queryClient)?.add(mutation);
      // Do not block hydration on an action that may be paused again. A future
      // confirmed author login or online/focus event can continue the same work.
      void mutation.continue().catch(() => {});
    } else {
      replayPermits.get(queryClient)?.delete(mutation);
      held.push(mutation);
    }
  }
  if (!currentId || !held.length) { lastRecoveryNotice.delete(queryClient); return; }
  // Focus, hydration and identity effects can all request recovery. Show one
  // notice for the same held work and employee, rather than stacking warnings.
  const signature = JSON.stringify([currentId ?? null, viewingAs, held.map(mutation => mutation.mutationId).sort((a, b) => a - b)]);
  if (lastRecoveryNotice.get(queryClient) === signature) return;
  lastRecoveryNotice.set(queryClient, signature);
  toast({ title: "Offline actions are paused", description: viewingAs
    ? "Viewing as another employee is view-only, so saved offline actions wait until the employee who queued them signs in themselves."
    : "Sign in as the employee who queued these actions. Older actions without an author need a manager to review the saved work before it is entered again.", variant: "destructive", severity: "warning" });
}
